import React from 'react';
import {
  Archive,
  Folder,
  FolderPlus,
  MoreHorizontal,
  Zap,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { renderText } from '../../utils/workflowLibraryUtils';
import {
  ALL_WORKFLOWS_FOLDER_ID,
  ARCHIVED_FOLDER_ID,
  UNFILED_FOLDER_ID,
} from '../../utils/workflowLibraryUtils';

function FolderRow({
  id,
  name,
  count,
  icon: Icon,
  collapsed,
  selected,
  onSelect,
  onAction,
  showMenu = true,
}) {
  return (
    <div className="group relative flex items-center gap-1">
      <button
        type="button"
        title={collapsed ? name : undefined}
        data-testid={`workflow-folder-row-${id}`}
        onClick={() => onSelect(id)}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors',
          selected
            ? 'bg-emerald-500/15 text-emerald-300'
            : 'text-zinc-400 hover:bg-zinc-800/80 hover:text-white',
          collapsed && 'justify-center px-2',
        )}
      >
        <Icon size={16} className="shrink-0" />
        {!collapsed ? (
          <>
            <span className="truncate">{renderText(name)}</span>
            <span className="ml-auto text-xs text-zinc-500">{renderText(count, '0')}</span>
          </>
        ) : null}
      </button>
      {!collapsed && showMenu && onAction ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid={`workflow-folder-menu-${id}`}
              className="rounded p-1 text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-800 hover:text-white group-hover:opacity-100"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700 w-44">
            <DropdownMenuItem
              className="text-zinc-300"
              onSelect={(e) => { e.preventDefault(); onAction('rename', id); }}
            >
              Rename folder
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-zinc-300"
              onSelect={(e) => { e.preventDefault(); onAction('move', id); }}
            >
              Move to folder
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-zinc-300"
              onSelect={(e) => { e.preventDefault(); onAction('permissions', id); }}
            >
              Manage permissions
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-700" />
            <DropdownMenuItem
              className="text-red-400"
              onSelect={(e) => { e.preventDefault(); onAction('delete', id); }}
            >
              Delete folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

const WorkflowFoldersSidebar = ({
  collapsed = false,
  folders = [],
  selectedFolderId,
  folderCounts = {},
  onSelectFolder,
  onCreateFolder,
  onFolderAction,
}) => (
  <div
    className={cn(
      'flex h-full min-h-0 flex-col border-r border-zinc-800 bg-zinc-950/60',
      collapsed ? 'px-2 py-3' : 'px-3 py-4',
    )}
    data-testid="workflow-folders-sidebar-content"
  >
    {!collapsed ? (
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Workflows</p>
          <p className="text-sm font-semibold text-white">Folders</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 border-zinc-700 text-zinc-300"
          data-testid="workflow-create-folder-btn"
          onClick={onCreateFolder}
        >
          <FolderPlus size={14} className="mr-1.5" />
          New
        </Button>
      </div>
    ) : (
      <button
        type="button"
        title="Create folder"
        data-testid="workflow-create-folder-btn"
        onClick={onCreateFolder}
        className="mb-3 flex h-9 w-full items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white"
      >
        <FolderPlus size={16} />
      </button>
    )}

    <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto">
      <FolderRow
        id={ALL_WORKFLOWS_FOLDER_ID}
        name="All workflows"
        count={folderCounts[ALL_WORKFLOWS_FOLDER_ID] || 0}
        icon={Zap}
        collapsed={collapsed}
        selected={selectedFolderId === ALL_WORKFLOWS_FOLDER_ID}
        onSelect={onSelectFolder}
        showMenu={false}
      />
      <FolderRow
        id={UNFILED_FOLDER_ID}
        name="Unfiled"
        count={folderCounts[UNFILED_FOLDER_ID] || 0}
        icon={Folder}
        collapsed={collapsed}
        selected={selectedFolderId === UNFILED_FOLDER_ID}
        onSelect={onSelectFolder}
        showMenu={false}
      />
      {folders.map((folder) => (
        <FolderRow
          key={folder.id}
          id={folder.id}
          name={renderText(folder.name)}
          count={folderCounts[folder.id] || 0}
          icon={Folder}
          collapsed={collapsed}
          selected={selectedFolderId === folder.id}
          onSelect={onSelectFolder}
          onAction={onFolderAction}
        />
      ))}
      <FolderRow
        id={ARCHIVED_FOLDER_ID}
        name="Archived"
        count={folderCounts[ARCHIVED_FOLDER_ID] || 0}
        icon={Archive}
        collapsed={collapsed}
        selected={selectedFolderId === ARCHIVED_FOLDER_ID}
        onSelect={onSelectFolder}
        showMenu={false}
      />
    </nav>
  </div>
);

export default WorkflowFoldersSidebar;
